import _ from 'lodash';
import moment from 'moment';

import formatDateLabel from 'components/V5Gadget/formatDateLabel';
import getDateBuckets from 'screens/GoalShow/getDateBuckets';

const getEndDate = ({
  startDate,
  cadence,
}: {
  startDate: string;
  cadence: 'week' | 'month';
}) => {
  if (cadence === 'week') {
    return moment(startDate).add({ day: 6 }).format('YYYY-MM-DD');
  }

  return moment(startDate).endOf('month').format('YYYY-MM-DD');
};

export const buildFiscalPeriods = ({
  scorecard,
  now,
  fiscalPeriods,
  currentFiscalPeriod,
}: {
  scorecard: Scorecards.Scorecard;
  now: string;
  fiscalPeriods: FixedPeriods.FiscalPeriod[];
  currentFiscalPeriod: FixedPeriods.FiscalPeriod | undefined;
}): Period[] => {
  const numberOfPeriods = scorecard.defaultSelectedNumberOfPeriods || 0;
  const toPeriod = (p: FixedPeriods.FiscalPeriod): Period => ({
    startDate: p.startDate,
    endDate: p.endDate,
    label: `${p.startDate} - ${p.endDate}`,
  });

  // Legacy: scorecards saved before numberOfFuturePeriods existed keep their
  // original window.
  if (scorecard.numberOfFuturePeriods === undefined) {
    // With no explicit start, anchor a rolling window ending on the current
    // fiscal period (last N periods).
    const startPeriodIndex = scorecard.startDate
      ? fiscalPeriods.findIndex((p) => p.startDate === scorecard.startDate)
      : Math.max(
          0,
          fiscalPeriods.findIndex(
            (p) => p.startDate === currentFiscalPeriod?.startDate,
          ) -
            (numberOfPeriods - 1),
        );
    return fiscalPeriods
      .filter(
        (p, idx) =>
          idx - startPeriodIndex >= 0 &&
          idx - startPeriodIndex < numberOfPeriods &&
          (!scorecard.ignoreFuturePeriods || p.startDate <= now),
      )
      .map(toPeriod);
  }

  // -1 when no fiscal period has started yet, so only future periods show
  const currentPeriodIndex = _.findLastIndex(
    fiscalPeriods,
    (p) => p.startDate <= now,
  );

  const fixedStartIndex = scorecard.startDate
    ? fiscalPeriods.findIndex((p) => p.startDate === scorecard.startDate)
    : -1;
  const startPeriodIndex =
    fixedStartIndex !== -1
      ? fixedStartIndex
      : Math.max(0, currentPeriodIndex - (numberOfPeriods - 1));
  const endPeriodIndex = currentPeriodIndex + scorecard.numberOfFuturePeriods;

  return fiscalPeriods
    .slice(startPeriodIndex, endPeriodIndex + 1)
    .map(toPeriod);
};

const buildScorecardPeriods = ({
  scorecard,
  now,
  toIsoDate,
  weekStartsOn,
  fiscalPeriods,
  currentFiscalPeriod,
}: {
  scorecard: Scorecards.Scorecard;
  now: string;
  toIsoDate: (date: Scorecards.WeekDate | Scorecards.IsoDate) => string;
  weekStartsOn: WeekStartsOn;
  fiscalPeriods: FixedPeriods.FiscalPeriod[];
  currentFiscalPeriod: FixedPeriods.FiscalPeriod | undefined;
}): {
  periods: Period[];
  pastPeriods: Period[];
  futurePeriods: Period[];
} => {
  const periods = (() => {
    if (scorecard.cadence === 'fiscalPeriod') {
      return buildFiscalPeriods({
        scorecard,
        now,
        fiscalPeriods,
        currentFiscalPeriod,
      });
    }

    const { cadence } = scorecard;
    const numberOfFuturePeriods = scorecard.numberOfFuturePeriods || 0;

    // With no explicit start, show a rolling window of the last N periods.
    const startDate = scorecard.startDate
      ? toIsoDate(scorecard.startDate)
      : moment(now)
          .subtract(
            (scorecard.defaultSelectedNumberOfPeriods || 12) - 1,
            cadence,
          )
          .format('YYYY-MM-DD');

    // getDateBuckets excludes weeks starting on endDate, so nudge the end
    // forward a day to keep the last future week on week-start days. With no
    // future periods, endDate stays as now so existing lists are unchanged.
    const endDate =
      numberOfFuturePeriods === 0
        ? now
        : moment(now)
            .add(numberOfFuturePeriods, cadence)
            .add(cadence === 'week' ? 1 : 0, 'day')
            .format('YYYY-MM-DD');

    return getDateBuckets({
      startDate,
      endDate,
      interval: cadence,
      weekStartsOn: scorecard.weekStartsOnOverride || weekStartsOn,
    })
      .sort()
      .map((bucketStartDate) => {
        const dateRange = {
          startDate: bucketStartDate,
          endDate: getEndDate({ startDate: bucketStartDate, cadence }),
        };
        return {
          ...dateRange,
          label: formatDateLabel(
            dateRange.startDate,
            cadence,
            false,
            dateRange,
            false,
            false,
          ),
        };
      });
  })();

  return {
    periods,
    pastPeriods: periods.filter((p) => p.startDate <= now),
    futurePeriods: periods.filter((p) => p.startDate > now),
  };
};

export default buildScorecardPeriods;
